/**
 * Skin3DPainter.js  —  Three.js 3D Skin Painter
 *
 * Props:
 *   imageData  {ImageData}   — 64x64 aktueller Skin (IMMER aktuell halten)
 *   model      {string}      — 'classic' | 'slim'
 *   tool       {string}      — 'pencil' | 'eraser' | 'fill' | 'picker'
 *   color      {[r,g,b,a]}
 *   brushSize  {number}
 *   onPaint    {fn(ImageData)} — wird nach jedem Strich aufgerufen
 *   onColorPick {fn([r,g,b,a])}
 */

import React, {
  useRef, useEffect, useCallback, forwardRef, useImperativeHandle,
} from 'react';
import * as THREE from 'three';

// ─── Minecraft 64×64 UV-Koordinaten ────────────────────────────────────────
// Face-Reihenfolge bei BoxGeometry: +x(right), -x(left), +y(top), -y(bottom), +z(front), -z(back)
// Mit half-pixel offset an den Grenzen, damit UV-Werte nicht exakt auf Pixel-Grenzen fallen.
// Pixel x hat UV-Range [x/64, (x+1)/64), daher offset: +0.5 und -0.5 an Min/Max.
const S = 64;
function uv(x, y, w, h) {
  const u0 = (x + 0.5) / S;
  const v0 = 1 - (y + h - 0.5) / S;
  const u1 = (x + w - 0.5) / S;
  const v1 = 1 - (y + 0.5) / S;
  return [u0, v0, u1, v1];  // [u0,v0,u1,v1]
}

const UVS = {
  head:            { right:uv(0,8,8,8),   left:uv(16,8,8,8),  top:uv(8,0,8,8),   bottom:uv(16,0,8,8), front:uv(8,8,8,8),   back:uv(24,8,8,8)  },
  head_overlay:    { right:uv(32,8,8,8),  left:uv(48,8,8,8),  top:uv(40,0,8,8),  bottom:uv(48,0,8,8), front:uv(40,8,8,8),  back:uv(56,8,8,8)  },
  body:            { right:uv(16,20,4,12),left:uv(28,20,4,12),top:uv(20,16,8,4), bottom:uv(28,16,8,4),front:uv(20,20,8,12),back:uv(32,20,8,12) },
  body_overlay:    { right:uv(16,36,4,12),left:uv(28,36,4,12),top:uv(20,32,8,4), bottom:uv(28,32,8,4),front:uv(20,36,8,12),back:uv(32,36,8,12) },
  arm_right:       { right:uv(40,20,4,12),left:uv(48,20,4,12),top:uv(44,16,4,4), bottom:uv(48,16,4,4),front:uv(44,20,4,12),back:uv(52,20,4,12) },
  arm_right_slim:  { right:uv(40,20,4,12),left:uv(47,20,4,12),top:uv(44,16,3,4), bottom:uv(47,16,3,4),front:uv(44,20,3,12),back:uv(51,20,3,12) },
  arm_left:        { right:uv(32,52,4,12),left:uv(40,52,4,12),top:uv(36,48,4,4), bottom:uv(40,48,4,4),front:uv(36,52,4,12),back:uv(44,52,4,12) },
  arm_left_slim:   { right:uv(32,52,4,12),left:uv(39,52,4,12),top:uv(36,48,3,4), bottom:uv(39,48,3,4),front:uv(36,52,3,12),back:uv(43,52,3,12) },
  leg_right:       { right:uv(0,20,4,12), left:uv(8,20,4,12), top:uv(4,16,4,4),  bottom:uv(8,16,4,4), front:uv(4,20,4,12), back:uv(12,20,4,12) },
  leg_right_overlay:{ right:uv(0,36,4,12), left:uv(8,36,4,12), top:uv(4,32,4,4), bottom:uv(8,32,4,4), front:uv(4,36,4,12), back:uv(12,36,4,12) },
  leg_left:        { right:uv(16,52,4,12),left:uv(24,52,4,12),top:uv(20,48,4,4), bottom:uv(24,48,4,4),front:uv(20,52,4,12),back:uv(28,52,4,12) },
  leg_left_overlay:{ right:uv(0,52,4,12), left:uv(8,52,4,12), top:uv(4,48,4,4),  bottom:uv(8,48,4,4), front:uv(4,52,4,12), back:uv(12,52,4,12) },
};

function applyUVs(geo, uvDef) {
  const a = geo.getAttribute('uv');
  const faces = [uvDef.right, uvDef.left, uvDef.top, uvDef.bottom, uvDef.front, uvDef.back];
  faces.forEach(([u0,v0,u1,v1], fi) => {
    const b = fi * 4;
    a.setXY(b+0, u1, v1); a.setXY(b+1, u0, v1);
    a.setXY(b+2, u1, v0); a.setXY(b+3, u0, v0);
  });
  a.needsUpdate = true;
}

function box(w, h, d, uvDef) {
  const g = new THREE.BoxGeometry(w, h, d);
  applyUVs(g, uvDef);
  return g;
}

// Minecraft-Proportionen: 1 Block = 1 Einheit im Modell-Raum
// Figur-Hoehe: Kopfoberseite bei y=24px, Fuss-Unterseite bei y=-12px → 36px = 2.25 Einheiten (bei u=1/16)
// Skalierungsfaktor so waehlen, dass die Figur ~0.8 Einheiten hoch ist → SCALE = 0.8/2.25 ≈ 0.355
const MODEL_SCALE = 0.355;

function buildModel(tex, model) {
  const slim = model === 'slim';
  const u = 1/16;
  const mat = () => new THREE.MeshStandardMaterial({ map: tex, transparent: true, alphaTest: 0.01, side: THREE.FrontSide });
  const g = new THREE.Group();

  // Kopf
  const head = new THREE.Mesh(box(8*u,8*u,8*u,   UVS.head), mat());
  head.position.y = 20*u; g.add(head);
  const headOv = new THREE.Mesh(box(8.5*u,8.5*u,8.5*u, UVS.head_overlay), mat());
  headOv.position.y = 20*u; g.add(headOv);

  // Körper
  const body = new THREE.Mesh(box(8*u,12*u,4*u,  UVS.body), mat());
  body.position.y = 6*u; g.add(body);
  const bodyOv = new THREE.Mesh(box(8.5*u,12.5*u,4.5*u, UVS.body_overlay), mat());
  bodyOv.position.y = 6*u; g.add(bodyOv);

  // Arme
  const aw = slim ? 3 : 4;
  const armRUV = slim ? UVS.arm_right_slim : UVS.arm_right;
  const armLUV = slim ? UVS.arm_left_slim  : UVS.arm_left;
  const armR = new THREE.Mesh(box(aw*u,12*u,4*u, armRUV), mat());
  armR.position.set( (6+aw/2)*u, 6*u, 0); g.add(armR);
  const armL = new THREE.Mesh(box(aw*u,12*u,4*u, armLUV), mat());
  armL.position.set(-(6+aw/2)*u, 6*u, 0); g.add(armL);

  // Beine
  const legR = new THREE.Mesh(box(4*u,12*u,4*u,  UVS.leg_right), mat());
  legR.position.set( 2*u, -6*u, 0); g.add(legR);
  const legROv = new THREE.Mesh(box(4.5*u,12.5*u,4.5*u, UVS.leg_right_overlay), mat());
  legROv.position.set( 2*u, -6*u, 0); g.add(legROv);
  const legL = new THREE.Mesh(box(4*u,12*u,4*u,  UVS.leg_left), mat());
  legL.position.set(-2*u, -6*u, 0); g.add(legL);
  const legLOv = new THREE.Mesh(box(4.5*u,12.5*u,4.5*u, UVS.leg_left_overlay), mat());
  legLOv.position.set(-2*u, -6*u, 0); g.add(legLOv);

  // Skalieren und vertikal zentrieren:
  // Ungekuerzte Mitte liegt bei y = (24u - 12u) / 2 = 6u
  // Nach Skalierung: Mitte = 6u * MODEL_SCALE → als position.y negieren
  g.scale.setScalar(MODEL_SCALE);
  g.position.y = -6 * u * MODEL_SCALE;

  return g;
}

// ─── Hauptkomponente ────────────────────────────────────────────────────────
const Skin3DPainter = forwardRef(({
  imageData,       // ImageData 64×64 — IMMER aktuell
  model = 'classic',
  tool = 'pencil',
  color = [0,0,0,255],
  brushSize = 1,
  onPaint,         // fn(ImageData) — Live-Sync, kein History-Push
  onStrokeEnd,     // fn(ImageData) — nur bei pointerUp, fuer History
  onColorPick,     // fn([r,g,b,a])
}, ref) => {

  const mountRef   = useRef(null);
  const rendRef    = useRef(null);
  const sceneRef   = useRef(null);
  const camRef     = useRef(null);
  const groupRef   = useRef(null);
  const texRef     = useRef(null);   // THREE.CanvasTexture
  const canvasRef  = useRef(null);   // 64×64 Painting-Canvas
  const ctxRef     = useRef(null);   // 2D Context (immer gleiche Instanz)
  const rafRef     = useRef(null);
  const didPaintRef = useRef(false);

  const rotRef   = useRef({ y: 0.3, x: -0.15 });
  const zoomRef  = useRef(1.1);  // Startzoom: ganze Figur sichtbar
  const orbitRef = useRef({ on: false, lx: 0, ly: 0 });
  const paintRef = useRef({ on: false });
  const rcRef    = useRef(new THREE.Raycaster());
  const mvRef    = useRef(new THREE.Vector2());

  // ── Init scene (once) ────────────────────────────────────────────────────
  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const w = el.clientWidth || 500, h = el.clientHeight || 600;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x222233);
    sceneRef.current = scene;

    const cam = new THREE.PerspectiveCamera(40, w/h, 0.01, 100);
    cam.position.z = 1.1;
    camRef.current = cam;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    rendRef.current = renderer;

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const d1 = new THREE.DirectionalLight(0xffffff, 0.85);
    d1.position.set(4, 6, 4); scene.add(d1);
    const d2 = new THREE.DirectionalLight(0x9999ff, 0.3);
    d2.position.set(-4, -3, -4); scene.add(d2);

    // Render loop
    const tick = () => { rafRef.current = requestAnimationFrame(tick); renderer.render(scene, cam); };
    tick();

    const onResize = () => {
      if (!mountRef.current) return;
      const nw = mountRef.current.clientWidth, nh = mountRef.current.clientHeight;
      cam.aspect = nw/nh; cam.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, []);

  // ── Wenn imageData sich ändert → Canvas + Textur aktualisieren ───────────
  useEffect(() => {
    if (!imageData || !sceneRef.current) return;

    if (!canvasRef.current) {
      // Ersten Canvas + Textur erstellen
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      canvasRef.current = canvas;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctxRef.current = ctx;
      ctx.putImageData(imageData, 0, 0);

      const tex = new THREE.CanvasTexture(canvas);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      texRef.current = tex;

      // Modell bauen
      const grp = buildModel(tex, model);
      grp.rotation.y = rotRef.current.y;
      grp.rotation.x = rotRef.current.x;
      sceneRef.current.add(grp);
      groupRef.current = grp;
    } else {
      // Nur Canvas-Inhalt updaten
      ctxRef.current.putImageData(imageData, 0, 0);
      if (texRef.current) texRef.current.needsUpdate = true;
    }
  }, [imageData, model]);

  // Modell neu bauen wenn model (classic/slim) wechselt
  useEffect(() => {
    if (!sceneRef.current || !texRef.current || !groupRef.current) return;
    sceneRef.current.remove(groupRef.current);
    groupRef.current.traverse(c => c.geometry?.dispose());
    const grp = buildModel(texRef.current, model);
    grp.rotation.y = rotRef.current.y;
    grp.rotation.x = rotRef.current.x;
    sceneRef.current.add(grp);
    groupRef.current = grp;
  }, [model]);

  // ── Paint-Helfer ─────────────────────────────────────────────────────────
  const doRaycast = useCallback((cx, cy) => {
    if (!mountRef.current || !groupRef.current) return null;
    const r = mountRef.current.getBoundingClientRect();
    mvRef.current.x =  ((cx - r.left) / r.width)  * 2 - 1;
    mvRef.current.y = -((cy - r.top)  / r.height) * 2 + 1;
    rcRef.current.setFromCamera(mvRef.current, camRef.current);
    const hits = rcRef.current.intersectObject(groupRef.current, true);
    return hits[0] ?? null;
  }, []);

   const paintAtUV = useCallback((uvx, uvy) => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = ctxRef.current || c.getContext('2d', { willReadFrequently: true });
    // UV ist jetzt mit half-pixel offset → direkt runden sollte richtig liegen
    const px = Math.max(0, Math.min(63, Math.round(uvx * 64 - 0.5)));
    const py = Math.max(0, Math.min(63, Math.round((1 - uvy) * 64 - 0.5)));
    const r = Math.max(0, brushSize - 1);
    const [cr, cg, cb, ca] = color;

    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx*dx+dy*dy > r*r+r) continue;
        const x = px+dx, y = py+dy;
        if (x<0||x>=64||y<0||y>=64) continue;
        if (tool === 'pencil') {
          ctx.globalAlpha = ca/255;
          ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
          ctx.fillRect(x, y, 1, 1);
          ctx.globalAlpha = 1;
        } else if (tool === 'eraser') {
          ctx.clearRect(x, y, 1, 1);
        }
      }
    }
    if (texRef.current) texRef.current.needsUpdate = true;
    didPaintRef.current = true;
    if (onPaint) onPaint(ctx.getImageData(0, 0, 64, 64));
  }, [tool, color, brushSize, onPaint]);

  const fillAtUV = useCallback((uvx, uvy) => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = ctxRef.current || c.getContext('2d', { willReadFrequently: true });
    const px = Math.max(0, Math.min(63, Math.round(uvx * 64 - 0.5)));
    const py = Math.max(0, Math.min(63, Math.round((1-uvy)*64 - 0.5)));
    const imgData = ctx.getImageData(0,0,64,64);
    const d = imgData.data;
    const ti = (py*64+px)*4;
    const tr=d[ti],tg=d[ti+1],tb=d[ti+2],ta=d[ti+3];
    const [fr,fg,fb,fa] = color;
    if (tr===fr&&tg===fg&&tb===fb&&ta===fa) return;
    const stack=[[px,py]]; const vis=new Uint8Array(64*64);
    while(stack.length){
      const [x,y]=stack.pop();
      if(x<0||x>=64||y<0||y>=64) continue;
      const i=y*64+x; if(vis[i]) continue;
      const pi=i*4;
      if(d[pi]!==tr||d[pi+1]!==tg||d[pi+2]!==tb||d[pi+3]!==ta) continue;
      vis[i]=1; d[pi]=fr;d[pi+1]=fg;d[pi+2]=fb;d[pi+3]=fa;
      stack.push([x+1,y],[x-1,y],[x,y+1],[x,y-1]);
    }
    ctx.putImageData(imgData,0,0);
    if(texRef.current) texRef.current.needsUpdate=true;
    didPaintRef.current = true;
    if(onPaint) onPaint(ctx.getImageData(0,0,64,64));
  }, [color, onPaint]);

  const pickAtUV = useCallback((uvx, uvy) => {
    const c = canvasRef.current;
    if (!c || !onColorPick) return;
    const ctx = ctxRef.current || c.getContext('2d', { willReadFrequently: true });
    const px = Math.max(0, Math.min(63, Math.round(uvx * 64 - 0.5)));
    const py = Math.max(0, Math.min(63, Math.round((1-uvy)*64 - 0.5)));
    const p = ctx.getImageData(px, py, 1, 1).data;
    onColorPick([p[0],p[1],p[2],p[3]]);
  }, [onColorPick]);

  // ── Pointer-Events ───────────────────────────────────────────────────────
  const onDown = useCallback((e) => {
    if (e.button === 2 || e.shiftKey) {
      orbitRef.current = { on:true, lx:e.clientX, ly:e.clientY };
    } else if (e.button === 0) {
      paintRef.current.on = true;
      didPaintRef.current = false;
      const hit = doRaycast(e.clientX, e.clientY);
      if (hit?.uv) {
        if      (tool==='fill')   fillAtUV(hit.uv.x, hit.uv.y);
        else if (tool==='picker') pickAtUV(hit.uv.x, hit.uv.y);
        else                      paintAtUV(hit.uv.x, hit.uv.y);
      }
    }
  }, [tool, doRaycast, paintAtUV, fillAtUV, pickAtUV]);

  const onMove = useCallback((e) => {
    if (orbitRef.current.on) {
      const dx = e.clientX - orbitRef.current.lx;
      const dy = e.clientY - orbitRef.current.ly;
      orbitRef.current.lx = e.clientX; orbitRef.current.ly = e.clientY;
      rotRef.current.y += dx * 0.012;
      rotRef.current.x = Math.max(-Math.PI/2, Math.min(Math.PI/2, rotRef.current.x + dy*0.012));
      if (groupRef.current) {
        groupRef.current.rotation.y = rotRef.current.y;
        groupRef.current.rotation.x = rotRef.current.x;
      }
    } else if (paintRef.current.on && (tool==='pencil'||tool==='eraser')) {
      const hit = doRaycast(e.clientX, e.clientY);
      if (hit?.uv) paintAtUV(hit.uv.x, hit.uv.y);
    }
  }, [doRaycast, paintAtUV, tool]);

  const onUp = useCallback(() => {
    orbitRef.current.on = false;
    const wasPainting = paintRef.current.on;
    paintRef.current.on = false;
    if (wasPainting && didPaintRef.current && onStrokeEnd) {
      const ctx = ctxRef.current;
      if (ctx) onStrokeEnd(ctx.getImageData(0, 0, 64, 64));
    }
    didPaintRef.current = false;
  }, [onStrokeEnd]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    zoomRef.current = Math.max(0.3, Math.min(3.0, zoomRef.current + e.deltaY*0.002));
    if (camRef.current) camRef.current.position.z = zoomRef.current;
  }, []);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const preventCtx = e => e.preventDefault();
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup',   onUp);
    el.addEventListener('pointerleave',onUp);
    el.addEventListener('wheel', onWheel, {passive:false});
    el.addEventListener('contextmenu', preventCtx);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup',   onUp);
      el.removeEventListener('pointerleave',onUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', preventCtx);
    };
  }, [onDown, onMove, onUp, onWheel]);

  // ── Imperative handle ────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    updateImageData: (imgData) => {
      if (!canvasRef.current) return;
      const ctx = ctxRef.current || canvasRef.current.getContext('2d', { willReadFrequently: true });
      ctx.putImageData(imgData, 0, 0);
      if (texRef.current) texRef.current.needsUpdate = true;
    },
  }), []);

  const cursor = { pencil:'crosshair', eraser:'cell', fill:'copy', picker:'crosshair' }[tool] ?? 'crosshair';

  return (
    <div ref={mountRef} style={{
      width:'100%', height:'100%',
      cursor, userSelect:'none', touchAction:'none',
    }}/>
  );
});

Skin3DPainter.displayName = 'Skin3DPainter';
export default Skin3DPainter;
